import { useState, useEffect } from "react";
import { WorksList } from "./WorksList";
import { api, IMAGE_PROCESSOR_URL } from "../../lib/api";

export interface WorkGroup {
  id: number;
  name: string;
  icon_path: string | null;
  display_order: number;
  is_visible: boolean;
  created_at: string;
  updated_at: string;
}

interface IconOption {
  title: string;
  storage_path: string;
}

export function WorksManagement() {
  const [workGroups, setWorkGroups] = useState<WorkGroup[]>([]);
  const [icons, setIcons] = useState<IconOption[]>([]);
  const [newGroup, setNewGroup] = useState({
    name: "",
    icon_path: "",
    display_order: 0,
    is_visible: true,
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchIcons = async () => {
    try {
      type IllustrationRow = { id: string; title: string; storage_path: string };
      const all = (await api.getIllustrations()) as IllustrationRow[];
      const iconList = all
        .filter((i) => typeof i.storage_path === "string" && i.storage_path.toLowerCase().startsWith("icon/"))
        .map((i) => ({ title: i.title, storage_path: i.storage_path }))
        .sort((a, b) => (a.title || "").localeCompare(b.title || ""));
      setIcons(iconList);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
        setTimeout(() => setError(null), 5000);
      }
    }
  };

  const fetchWorkGroups = async () => {
    try {
      const data = await api.getWorks(true);
      setWorkGroups(
        [...data].sort((a, b) => a.display_order - b.display_order) as WorkGroup[]
      );
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
        setTimeout(() => setError(null), 5000);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      await api.createWorks(newGroup);

      setSuccess("グループを追加しました");
      setTimeout(() => setSuccess(null), 3000);

      setNewGroup({
        name: "",
        icon_path: "",
        display_order: workGroups.length,
        is_visible: true,
      });

      fetchWorkGroups();
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
        setTimeout(() => setError(null), 5000);
      }
    }
  };

  useEffect(() => {
    fetchWorkGroups();
    fetchIcons();
  }, []);

  return (
    <div className="p-4">
      <div className="space-y-8">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative">
            {success}
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className="border rounded-md p-4 space-y-4"
        >
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              グループ名 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={newGroup.name}
              onChange={(e) =>
                setNewGroup({ ...newGroup, name: e.target.value })
              }
              placeholder="group1, group2 など"
              className="mt-1 p-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              required
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              アイコン
            </label>
            <select
              value={newGroup.icon_path}
              onChange={(e) =>
                setNewGroup({ ...newGroup, icon_path: e.target.value })
              }
              className="mt-1 p-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="">アイコンを選択してください</option>
              {icons.map((icon) => (
                <option key={icon.storage_path} value={icon.storage_path}>
                  {icon.title}
                </option>
              ))}
            </select>
            {newGroup.icon_path && (
              <div className="mt-2">
                <img
                  src={`${IMAGE_PROCESSOR_URL}/?path=${encodeURIComponent(newGroup.icon_path)}&width=128`}
                  alt={newGroup.icon_path}
                  className="w-16 h-16 object-contain"
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              表示順 <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={newGroup.display_order}
              onChange={(e) =>
                setNewGroup({
                  ...newGroup,
                  display_order: parseInt(e.target.value),
                })
              }
              className="mt-1 p-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              required
            />
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">
              表示/非表示
            </label>
            <input
              type="checkbox"
              checked={newGroup.is_visible}
              onChange={(e) =>
                setNewGroup({ ...newGroup, is_visible: e.target.checked })
              }
              className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>

          <button
            type="submit"
            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            グループを追加
          </button>
        </form>

        <h2 className="text-xl font-semibold text-center text-gray-700">
          登録済みグループ一覧
        </h2>
        <WorksList
          workGroups={workGroups}
          icons={icons}
          onUpdate={fetchWorkGroups}
        />
      </div>
    </div>
  );
}
